import { Auth0Provider, AppState, useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const UnauthorizedListener = () => {
  const { isAuthenticated, logout } = useAuth0();
  const loggingOut = useRef(false);

  useEffect(() => {
    const handler = () => {
      if (!isAuthenticated || loggingOut.current) return;
      loggingOut.current = true;
      try {
        logout({ logoutParams: { returnTo: `${window.location.origin}/login` } });
      } catch (err) {
        console.error('[UnauthorizedListener] logout failed, falling back to redirect:', err);
        window.location.href = '/login';
      }
    };
    window.addEventListener('invirtus:auth-unauthorized', handler);
    return () => window.removeEventListener('invirtus:auth-unauthorized', handler);
  }, [isAuthenticated, logout]);

  return null;
};

const Auth0ProviderWithNavigate = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE || '';
  const redirectUri = `${window.location.origin}/auth-callback`;

  const onRedirectCallback = (appState?: AppState) => {
    const target = appState?.returnTo || '/auth-callback';
    navigate(target);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: 'openid profile email',
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      <UnauthorizedListener />
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithNavigate;
